import React, { useState } from 'react';
import { exportData } from '../../utils/dataExport';

/**
 * Export Button Component for Cost Explorer
 * Produces clean, properly structured export with separate sections
 * AC 3: Support export in multiple formats
 */

type ExportFormatOption = 'csv' | 'json' | 'excel' | 'focus' | 'parquet';

interface ExportButtonProps {
  data?: any;
  filename?: string;
  disabled?: boolean;
  /** Human-readable name of the currently selected group-by dimension (e.g.
   * "Region", "Tag: CostCenter", "Resource") - used to label the breakdown
   * export correctly instead of always calling it "service", which was
   * wrong whenever the user had grouped by anything else. */
  dimensionLabel?: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename = 'cost-explorer-export',
  disabled = false,
  dimensionLabel = 'Service',
}) => {
  // A safe column/sheet-name key derived from the label, e.g. "Tag: CostCenter" -> "tag_costcenter".
  const dimensionKey = dimensionLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'dimension';
  const [isExporting, setIsExporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleExport = (format: ExportFormatOption) => {
    setIsExporting(true);
    setShowMenu(false);

    try {
      if (!data) {
        alert('No data available to export. Please run a query first.');
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const fullFilename = `${filename}-${timestamp}`;
      const currency = data.currency || 'USD';

      // --- Clean time series rows: merged by date with a column per provider ---
      // by_provider (real per-provider breakdown from the API) replaces the
      // old "guess AWS vs Azure from timestamp format" heuristic, which had
      // two real bugs: it never checked for GCP at all (GCP's cost was
      // silently lumped into the aws_cost column, mislabeling it), and it
      // relied on Athena's date_trunc('day', ...) happening to return a
      // timestamp-with-time string that differed from GCP/Azure's own plain
      // date format - a coincidence, not a real provider signal.
      const timeSeriesMap = new Map<string, { date: string; aws_cost: string; azure_cost: string; gcp_cost: string; total_cost: string; currency: string }>();

      (data.timeSeries || []).forEach((p: any) => {
        const rawDate = p.timestamp || p.date || '';
        // Normalize to YYYY-MM-DD
        const date = rawDate.length > 10 ? new Date(rawDate).toISOString().split('T')[0] : rawDate;

        if (!timeSeriesMap.has(date)) {
          timeSeriesMap.set(date, { date, aws_cost: '0.0000', azure_cost: '0.0000', gcp_cost: '0.0000', total_cost: '0.0000', currency: p.currency || currency });
        }
        const entry = timeSeriesMap.get(date)!;
        const byProvider = p.by_provider || {};
        entry.aws_cost = (Number(entry.aws_cost) + Number(byProvider.aws || 0)).toFixed(4);
        entry.azure_cost = (Number(entry.azure_cost) + Number(byProvider.azure || 0)).toFixed(4);
        entry.gcp_cost = (Number(entry.gcp_cost) + Number(byProvider.gcp || 0)).toFixed(4);
        entry.total_cost = (Number(entry.aws_cost) + Number(entry.azure_cost) + Number(entry.gcp_cost)).toFixed(4);
      });

      const timeSeriesRows = Array.from(timeSeriesMap.values()).sort((a, b) => a.date.localeCompare(b.date));

      // --- Clean breakdown rows: dimension value, cost, percentage ---
      const breakdownRows = (data.byDimension || []).map((d: any) => ({
        [dimensionKey]: d.dimensionValue || d.dimension || 'Unknown',
        cost: Number(d.cost || 0).toFixed(4),
        'percentage (%)': Number(d.percentage || 0).toFixed(2),
        currency,
      }));

      if (format === 'parquet') {
        // Same daily-costs data as the CSV export's first file, encoded as a
        // real Parquet file (columnar, via hyparquet-writer - a pure-JS
        // writer, no WASM/build-step needed). Dynamic import matches the
        // existing xlsx pattern below so a load failure doesn't break every
        // other export format.
        import('hyparquet-writer').then(({ parquetWriteBuffer }) => {
          const arrayBuffer = parquetWriteBuffer({
            columnData: [
              { name: 'date', data: timeSeriesRows.map(r => r.date), type: 'STRING' },
              { name: 'aws_cost', data: timeSeriesRows.map(r => Number(r.aws_cost)), type: 'DOUBLE' },
              { name: 'azure_cost', data: timeSeriesRows.map(r => Number(r.azure_cost)), type: 'DOUBLE' },
              { name: 'gcp_cost', data: timeSeriesRows.map(r => Number(r.gcp_cost)), type: 'DOUBLE' },
              { name: 'total_cost', data: timeSeriesRows.map(r => Number(r.total_cost)), type: 'DOUBLE' },
              { name: 'currency', data: timeSeriesRows.map(r => r.currency), type: 'STRING' },
            ],
          });
          const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${fullFilename}.parquet`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }).catch((err) => {
          console.error('Parquet export failed:', err);
          alert('Parquet export failed. Please try again.');
        });

      } else if (format === 'focus') {
        // Best-effort FOCUS 1.0 (FinOps Open Cost and Usage Specification)
        // export. Built from the daily time series - the most granular real
        // data this page has - rather than the period-total dimension
        // breakdown, since FOCUS is a row-per-charge-period format. Columns
        // this data genuinely supports are populated for real; there's no
        // per-charge SKU/ResourceId/ChargeCategory at this aggregation level
        // (only the group-by-resource "custom dimension" view has resource
        // IDs, and only as period totals), so those FOCUS columns are
        // intentionally left blank rather than filled with fabricated values.
        // EffectiveCost equals BilledCost because neither tenant has active
        // Reserved Instances/Savings Plans to create a difference yet - see
        // the cost-view redistribution logic for when that stops being true.
        // One row per (date, provider) using the real by_provider breakdown -
        // more correct for FOCUS's row-per-charge model than guessing a
        // single provider per timestamp, and it naturally covers GCP too.
        const PROVIDER_NAMES: Record<string, string> = { aws: 'AWS', azure: 'Azure', gcp: 'GCP' };
        const focusRows: Record<string, string>[] = [];
        (data.timeSeries || []).forEach((p: any) => {
          const rawDate = p.timestamp || p.date || '';
          const date = rawDate.length > 10 ? new Date(rawDate).toISOString().split('T')[0] : rawDate;
          const byProvider = p.by_provider || {};
          for (const [key, cost] of Object.entries(byProvider)) {
            const costNum = Number(cost || 0);
            if (costNum <= 0) continue;
            focusRows.push({
              ChargePeriodStart: date,
              ChargePeriodEnd: date,
              ProviderName: PROVIDER_NAMES[key] || key,
              BillingCurrency: p.currency || currency,
              BilledCost: costNum.toFixed(4),
              EffectiveCost: costNum.toFixed(4),
              ListCost: costNum.toFixed(4),
              ChargeCategory: 'Usage',
            });
          }
        });
        exportData({ data: focusRows, filename: `${fullFilename}-focus`, format: 'csv' });

      } else if (format === 'json') {
        const jsonExport = {
          exported_at: new Date().toISOString(),
          currency,
          total_cost: data.totalCost,
          daily_costs: timeSeriesRows,
          breakdown_dimension: dimensionLabel,
          breakdown: breakdownRows,
        };
        const blob = new Blob([JSON.stringify(jsonExport, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fullFilename}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } else if (format === 'excel') {
        import('xlsx').then((XLSX) => {
          const workbook = XLSX.utils.book_new();

          // Sheet 1: Daily Costs (one row per day, merged AWS + Azure)
          if (timeSeriesRows.length > 0) {
            const ws1 = XLSX.utils.json_to_sheet(timeSeriesRows);
            ws1['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(workbook, ws1, 'Daily Costs');
          }

          // Sheet 2: Breakdown by the currently selected group-by dimension
          if (breakdownRows.length > 0) {
            const ws2 = XLSX.utils.json_to_sheet(breakdownRows);
            ws2['!cols'] = [{ wch: 45 }, { wch: 14 }, { wch: 16 }, { wch: 10 }];
            // Excel sheet names are capped at 31 characters.
            XLSX.utils.book_append_sheet(workbook, ws2, `By ${dimensionLabel}`.slice(0, 31));
          }

          // Sheet 3: By Provider Per Day (one row per date per provider,
          // from the real by_provider breakdown - includes GCP now too)
          const PROVIDER_NAMES: Record<string, string> = { aws: 'AWS', azure: 'Azure', gcp: 'GCP' };
          const byProviderRows: { date: string; provider: string; cost: string; currency: string }[] = [];
          (data.timeSeries || []).forEach((p: any) => {
            const rawDate = p.timestamp || p.date || '';
            const date = rawDate.length > 10 ? new Date(rawDate).toISOString().split('T')[0] : rawDate;
            const byProvider = p.by_provider || {};
            for (const [key, cost] of Object.entries(byProvider)) {
              const costNum = Number(cost || 0);
              if (costNum <= 0) continue;
              byProviderRows.push({
                date,
                provider: PROVIDER_NAMES[key] || key,
                cost: costNum.toFixed(4),
                currency: p.currency || currency,
              });
            }
          });
          // Sort by date then provider
          byProviderRows.sort((a, b) => a.date.localeCompare(b.date) || a.provider.localeCompare(b.provider));

          if (byProviderRows.length > 0) {
            const ws3 = XLSX.utils.json_to_sheet(byProviderRows);
            ws3['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(workbook, ws3, 'By Provider Per Day');
          }

          XLSX.writeFile(workbook, `${fullFilename}.xlsx`);
        });

      } else {
        // CSV — export daily costs (merged, one row per day)
        if (timeSeriesRows.length > 0) {
          exportData({ data: timeSeriesRows, filename: `${fullFilename}-daily`, format: 'csv' });
        }
        // CSV — export breakdown as second file
        if (breakdownRows.length > 0) {
          setTimeout(() => {
            exportData({ data: breakdownRows, filename: `${fullFilename}-breakdown`, format: 'csv' });
          }, 400);
        }
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formats: { key: ExportFormatOption; label: string; description: string }[] = [
    { key: 'csv', label: 'CSV', description: `Two files: daily costs + ${dimensionLabel.toLowerCase()} breakdown` },
    { key: 'json', label: 'JSON', description: 'Single file with daily + breakdown sections' },
    { key: 'excel', label: 'Excel (.xlsx)', description: `Three sheets: Daily Costs + By ${dimensionLabel} + By Provider Per Day` },
    { key: 'focus', label: 'FOCUS (CSV)', description: 'FinOps Open Cost and Usage Specification 1.0 column format' },
    { key: 'parquet', label: 'Parquet', description: 'Daily costs as a columnar Parquet file' },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={disabled || isExporting}
        className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary dark:hover:bg-primary"
        aria-label="Export cost data"
        aria-expanded={showMenu}
        aria-haspopup="true"
      >
        {isExporting ? (
          <>
            <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </>
        )}
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} aria-hidden="true" />
          <div
            className="absolute right-0 z-20 mt-2 w-72 rounded-md border border-border bg-card shadow-lg dark:border-border dark:bg-card"
            role="menu"
          >
            <div className="p-2">
              <div className="mb-2 px-3 py-2 text-xs font-semibold text-muted-foreground">
                Select Export Format
              </div>
              {formats.map((fmt) => (
                <button
                  key={fmt.key}
                  onClick={() => handleExport(fmt.key)}
                  className="flex w-full flex-col items-start rounded-md px-3 py-2 text-left hover:bg-secondary dark:hover:bg-secondary"
                  role="menuitem"
                >
                  <span className="font-medium text-foreground">{fmt.label}</span>
                  <span className="text-xs text-muted-foreground">{fmt.description}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
